// ChangeGoldValue.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";
import "./ChangeGoldValue.css";

const LABELS = ["999","24K","23K","22K","20K","18K","14K","9K","STONE"];

// Purity map
const PURITY = {
  "999": 0.995, 
  "24K": 0.985,
  "23K": 0,   // corrected 23K
  "22K": 0.900,
  "20K": 0.835,
  "18K": 0.700,
  "14K": 0.500,
  "9K":  0.300
};

const CONVERSION = 0.02646; // as in your sheet

const ChangeGoldValue = () => {
  const navigate = useNavigate();
  useEscapeToGoBack();

  const [goldCode, setGoldCode] = useState("");  // B2 reference
  const [goldValues, setGoldValues] = useState({
    "999": "", "24K": "", "23K": "", "22K": "",
    "20K": "", "18K": "", "14K": "", "9K": "", "STONE": ""
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const mapRecordToUi = (rec) => ({
    "999":   rec.gold1 ?? "",
    "24K":   rec.gold2 ?? "",
    "23K":   rec.gold3 ?? "",
    "22K":   rec.gold4 ?? "",
    "20K":   rec.gold5 ?? "",
    "18K":   rec.gold6 ?? "",
    "14K":   rec.gold7 ?? "",
    "9K":    rec.gold8 ?? "",
    "STONE": rec.gold9 ?? ""
  });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const { data: rec } = await axios.get("https://www.lianfoong.com/api/gold-prices/latest");
        setGoldValues(mapRecordToUi(rec));
        if (rec && typeof rec.goldCode === "number") {
          setGoldCode(String(rec.goldCode)); // current code in B2
        }
      } catch (e) {
        console.error(e);
        setErr("Could not load latest gold prices. Check backend.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fmt2 = (n) => (Math.round(n * 100) / 100).toFixed(2);

  const handleChange = (k, v) => {
    setGoldValues((prev) => ({ ...prev, [k]: v }));
  };

  // Recalculate values from purity × goldCode × conversion
  const handleCalculate = () => {
    const code = parseFloat(goldCode);
    if (!code || code <= 0) {
      alert("Please enter a valid Gold Code (B2).");
      return;
    }
    const next = { ...goldValues };
    for (const k of LABELS) {
      if (k === "STONE") continue;
      const purity = PURITY[k];
      if (!purity) continue;
      const val = code * CONVERSION * purity;
      next[k] = fmt2(val);
    }
    setGoldValues(next);
  };

  const handleSubmit = async () => {
    const codeNum = Number(goldCode);
    if (!Number.isFinite(codeNum)) {
      alert("Please enter a valid Gold Code (B2).");
      return;
    }

    const payload = Object.fromEntries(
      LABELS.map((k, idx) => [k, parseFloat(goldValues[k]) || 0])
    );
    payload.goldCode = codeNum; // update gold code too

    try {
      await axios.post("https://www.lianfoong.com/api/gold-prices", payload);
      alert("Gold code and values updated!");
      navigate("/dashboard");
    } catch (e) {
      console.error(e);
      alert("Failed to update gold values.");
    }
  };

  return (
    <div className="change-gold-container">
      <h2>Update Gold Values</h2>

      {err && <div style={{ color: "red", marginBottom: 8 }}>{err}</div>}
      {loading && <div>Loading…</div>}

      {/* Gold Code (B2) input */}
      <div className="base-row">
        <label htmlFor="goldCode">Gold Code (B2)</label>
        <input
          id="goldCode"
          type="number"
          value={goldCode}
          onChange={(e) => setGoldCode(e.target.value)}
          placeholder="e.g. 6054"
        />
        <button className="calc-button" onClick={handleCalculate}>Calculate</button>
      </div>

      <table>
        <thead>
          <tr>{LABELS.map((k) => <th key={k}>{k}</th>)}</tr>
        </thead>
        <tbody>
          <tr>
            {LABELS.map((k) => (
              <td key={k}>
                <input
                  type="number"
                  step="0.01"
                  value={goldValues[k]}
                  onChange={(e) => handleChange(k, e.target.value)}
                />
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <div className="button-group">
        <button className="submit-button" onClick={handleSubmit}>Update</button>
        <button className="cancel-button" onClick={() => navigate("/dashboard")}>Cancel</button>
      </div>
    </div>
  );
};

export default ChangeGoldValue;
